import {  Component, Input, OnInit, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { InputTextModule } from 'primeng/inputtext';
import { RadioButtonModule } from 'primeng/radiobutton';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DedupeDonor, DedupeField } from '../../models/AddDonationModels';
import { AddDonationService } from '../../services/add-donation.service';
import { SearchService } from '../../../global/services/data/search-service/searchservice';
import { MessageService } from 'primeng/api';

@Component({
  selector: 'app-donor-dedupe',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputTextModule,
    RadioButtonModule,
    ButtonModule,
    CardModule,
    HttpClientModule
  ],
  templateUrl: './donor-dedupe.component.html',
  styleUrls: ['./donor-dedupe.component.scss'],
})
export class DonorDedupeComponent implements OnInit {
  @Input() selectedDonors: DedupeDonor[] = [];
  form: FormGroup;
  fields: Array<DedupeField> = [
    { label: 'Donor ID', control: 'donorId', inputControl: 'donorIdInput', options: [] },
    { label: 'First Name', control: 'firstName', inputControl: 'firstNameInput', options: [] },
    { label: 'Last Name', control: 'lastName', inputControl: 'lastNameInput', options: [] },
    { label: 'Middle Name', control: 'middleName', inputControl: 'middleNameInput', options: [] },
    { label: 'Suffix', control: 'suffix', inputControl: 'suffixInput', options: [] },
    { label: 'Address1', control: 'address1', inputControl: 'addressInput1', options: [] },
    { label: 'Address2', control: 'address2', inputControl: 'addressInput2', options: [] },
    { label: 'City', control: 'city', inputControl: 'cityInput', options: [] },
    { label: 'Email', control: 'email', inputControl: 'emailInput', options: [] },
    { label: 'Home Phone', control: 'homePhone', inputControl: 'homePhoneInput', options: [] },
    { label: 'Cell Phone', control: 'cellPhone', inputControl: 'cellPhoneInput', options: [] },
    { label: 'Employer', control: 'employer', inputControl: 'employerInput', options: [] },
    { label: 'Occupation', control: 'occupation', inputControl: 'occupationInput', options: [] },
    { label: 'Office Phone', control: 'officePhone', inputControl: 'officePhoneInput', options: [] },
  ];

  constructor(
    private fb: FormBuilder,
    private addDonationService: AddDonationService,
    private searchSerivce: SearchService,
    private messageService: MessageService,
    private cdRef: ChangeDetectorRef
  ) {
    this.form = this.fb.group({});
    this.fields.forEach((field) => {
      this.form.addControl(field.control, this.fb.control(''));
      this.form.addControl(field.inputControl, this.fb.control(''));
    });
  }

  mapData(): void {
    const donorIds: Array<number> = this.selectedDonors.map((dnr: DedupeDonor) => {
      return dnr.DonorID;
    });
    this.addDonationService.getDedupeDonorInfo(donorIds).subscribe((data: Array<DedupeDonor>) => {
      this.selectedDonors = data;
      console.debug(this.selectedDonors);
      this.mapSelectedDonorsToFields();
    });
  }
  
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedDonors']) {
      this.mapData();
    }
  }

  ngOnInit(): void {
    console.debug(this.selectedDonors);
    this.mapData();
  }

  onCancel(): void {
    this.searchSerivce.emitCloseCompareDonorModal();
  }

  mapSelectedDonorsToFields(): void {
    if (!this.selectedDonors || this.selectedDonors.length === 0) {
      return;
    }

    // Find donor with lowest and highest DonorID
    const sortedDonors = [...this.selectedDonors].sort((a, b) => a.DonorID - b.DonorID);
    const lowestDonor = sortedDonors[0];
    const highestDonor = sortedDonors[sortedDonors.length - 1];

    this.fields.forEach((field) => {
      const donorFieldKey = this.mapFieldControlToDonorKey(field.control);
      if (donorFieldKey) {
        // Get unique values for this field from the selected donors
        field.options = Array.from(
          new Set(this.selectedDonors.map((donor) => donor[donorFieldKey as keyof DedupeDonor]).filter(Boolean))
        ) as string[];

        // Set form values: donorId from lowest, others from highest
        if (field.control === 'donorId') {
          // Set donorId to the lowest donorId
          this.form.get(field.control)?.setValue(lowestDonor?.parentDonorId?.toString());
        } else {
          // Set other fields to the value from the highest donorId
          if (highestDonor[donorFieldKey as keyof DedupeDonor] !== undefined) {
            this.form.get(field.control)?.setValue(
              highestDonor[donorFieldKey as keyof DedupeDonor] as string
            );
          }
        }
      }
    });

    console.debug('Mapped Fields:', this.fields);
    console.debug('Form values after auto-select:', this.form.value);
  }

  private mapFieldControlToDonorKey(control: string): string | null {
    const fieldMap: Record<string, string> = {
      donorId: 'parentDonorId',
      firstName: 'firstName',
      lastName: 'lastName',
      middleName: 'middleName',
      suffix: 'suffix',
      address1: 'address1',
      address2: 'address2',
      city: 'city',
      state: 'state',
      zipCode: 'zipCode',
      homePhone: 'homePhone',
      cellPhone: 'cellPhone',
      emailAddress: 'emailAddress',
      employer: 'employer',
      occupation: 'occupation',
      officePhone: 'officePhone'
    };
    return fieldMap[control] || null;
  }

  setInputValue(control: string, value: any, inputControl?: string): void {
    this.form.get(control)?.setValue(value);
    if (inputControl) {
      this.form.get(inputControl)?.setValue(value);
    }
    console.debug(this.form.value);
  }

  setFocus(inputControl: string): void {
    this.form.get(inputControl)?.setValue('');
  }

  onInputChange(field: DedupeField, value: string): void {
    this.form.get(field.inputControl)?.setValue(value);
    this.form.get(field.control)?.setValue(value);
    this.cdRef.detectChanges();
  }

  onCustomInputFocus(field: DedupeField): void {
    this.form.get(field.control)?.setValue('__custom__');
  }

  onCustomInputInput(field: DedupeField): void {
    this.form.get(field.control)?.setValue('__custom__');
  }

  saveMergedDonor(): void {
    console.debug(this.form.value);
    let dedupeRecord = { ...this.form.value };

    // Replace custom marker with actual input value for each field
    this.fields.forEach(field => {
      if (dedupeRecord[field.control] === '__custom__') {
        dedupeRecord[field.control] = dedupeRecord[field.inputControl];
      }
    });

    dedupeRecord.parentDonorId = Number(dedupeRecord.donorId);

    const childDonorId = this.fields.find(f => f.control === 'donorId')?.options
                          .find(donorId => donorId !== dedupeRecord.parentDonorId);
    dedupeRecord.childDonorid = childDonorId ?? null;

    const mergedDnrVM = {
      mergedDonor: dedupeRecord,
      donorAndGrpIds: {
        parentGrpDnrId: dedupeRecord.parentDonorId,
        childGrpDnrIds: [dedupeRecord.childDonorid]
      }
    }

    this.addDonationService.mergeDonors(mergedDnrVM).subscribe(() => {
      this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Donors merged successfully!' });
      this.searchSerivce.emitCloseCompareDonorModal();
    }, (error: any) => {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error while de-duping donors!' });
    });
  }
}
